import { useEffect, useId, useRef, useState } from "react";
import { ReleaseNotesApi } from "../api";
import { getCachedReleaseNotes, loadReleaseNotesCached, NotesLoad, parseNotes } from "../releaseNotes";
import { useFocusTrap } from "../useFocusTrap";

// Header button + modal. Notes are fetched when the modal first opens (the
// modal body only mounts while open) and cached for the session; no polling.
export const WhatsNew = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="min-h-10 sm:min-h-0 text-sm text-neutral-500">
        What's new
      </button>
      {open && <WhatsNewModal onClose={() => setOpen(false)} />}
    </>
  );
};

const WhatsNewModal = ({ onClose }: { onClose: () => void }) => {
  const [state, setState] = useState<NotesLoad | null>(getCachedReleaseNotes);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getCachedReleaseNotes()) return;
    let cancelled = false;
    loadReleaseNotesCached(() => ReleaseNotesApi.get()).then((r) => {
      if (!cancelled) setState(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useFocusTrap(dialogRef, true, onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-safe-modal">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded bg-white p-4 text-sm shadow outline-none"
      >
        <h2 id={titleId} className="font-semibold">
          What's new
        </h2>
        {state === null && <p className="mt-2 text-neutral-500">Loading…</p>}
        {state?.status === "error" && (
          <p role="alert" className="mt-2 text-red-600">
            Couldn't load release notes. Please try again later.
          </p>
        )}
        {state?.status === "empty" && <p className="mt-2 text-neutral-500">No release notes yet.</p>}
        {state?.status === "ready" &&
          state.notes.map((n) => (
            <section key={n.version} className="mt-3">
              <h3 className="font-medium">
                {n.version}
                {n.title ? ` — ${n.title}` : ""}
              </h3>
              {parseNotes(n.publicNotes).map((block, i) =>
                block.kind === "list" ? (
                  <ul key={i} className="mt-1 list-disc pl-5 text-neutral-700">
                    {block.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} className="mt-1 text-neutral-700">
                    {block.text}
                  </p>
                ),
              )}
            </section>
          ))}
        <div className="mt-3 flex justify-end">
          <button onClick={onClose} className="min-h-10 sm:min-h-0 rounded border px-3 py-1 sm:px-2">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
